/**
 * Read-only preview of a locked industry expert submission.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';

export default function IndustryExpertFeedbackPreview() {
  const { submissionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/industry-expert-feedback/submissions/${submissionId}`)
      .then((res) => setData(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.error?.message || 'Could not load this submission.'))
      .finally(() => setLoading(false));
  }, [submissionId]);

  const answers = Array.isArray(data?.answers) ? data.answers : [];

  return (
    <Layout>
      <div className="iex-form-page">
        <div className="genz-inner">
          <p className="text-xs uppercase tracking-[0.2em] text-teal-200/80 mb-2">Locked preview</p>
          <h1 className="text-3xl font-semibold text-white m-0">{data?.title || 'Industry expert feedback'}</h1>
          <p className="genz-lock mt-3">Read-only. Submitted industry expert feedback cannot be edited.</p>
          {error ? <div className="alert-error mt-4">{error}</div> : null}
          {loading ? <div className="genz-card mt-6">Loading preview…</div> : null}
          {data ? (
            <div className="genz-card mt-6 space-y-4">
              <p className="text-slate-300 m-0">
                {[data.cycle_name, data.subject_code, data.event_name, data.submitted_at ? new Date(data.submitted_at).toLocaleString() : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {answers.map((row) => (
                <div key={row.key} className="genz-criterion">
                  <div className="flex justify-between gap-3">
                    <p className="text-white font-medium m-0">{row.label || row.key}</p>
                    <span>{row.rating}/5</span>
                  </div>
                  {row.comments ? <p className="text-sm text-slate-300 m-0">{row.comments}</p> : null}
                </div>
              ))}
              {answers.length === 0 ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  <p className="text-slate-200 m-0">Experience {data.experience_rating ?? '—'}</p>
                  <p className="text-slate-200 m-0">Process {data.process_rating ?? '—'}</p>
                  <p className="text-slate-200 m-0">Support {data.support_rating ?? '—'}</p>
                </div>
              ) : null}
              {data.strengths ? <p className="text-slate-200 m-0"><span className="text-teal-200">Strengths: </span>{data.strengths}</p> : null}
              {data.improvements ? <p className="text-slate-200 m-0"><span className="text-sky-200">Improvements: </span>{data.improvements}</p> : null}
              {data.additional_comments ? <p className="text-slate-200 m-0"><span className="text-slate-400">Notes: </span>{data.additional_comments}</p> : null}
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
